import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import dataService from 'services/dataService';
import { apiUrlGenerator } from 'infrastructure/hooks/use-api';
import { API_PODS_ASSIGNMENTS } from 'services/dataService/resources/pods/constants';

const STALE_TIME_5_MIN = 5 * 60 * 1000;

export interface PodClinic {
  guid: string;
  name: string;
  state?: string;
}

export interface PodAssignment {
  guid: string;
  podName: string;
  podType: PodType;
  clinics?: PodClinic[];
}

interface PodAssignmentsResponse {
  pods: PodAssignment[];
}

export type PodType = 'CCM' | 'RPM';

export const getPodAssignmentsKey = (podType: PodType) => [
  'pod-assignments',
  podType,
];

export const usePodAssignments = (podType: PodType) => {
  const { data, isLoading, isError } = useQuery<
    PodAssignmentsResponse | undefined
  >({
    queryKey: getPodAssignmentsKey(podType),
    queryFn: async () => {
      const { data: responseData } =
        await dataService.getList<PodAssignmentsResponse>(
          apiUrlGenerator(API_PODS_ASSIGNMENTS, {
            podType,
            includeClinics: true,
          }),
        );
      return responseData ?? undefined;
    },
    staleTime: STALE_TIME_5_MIN,
  });

  const podMap = useMemo(() => {
    const map = new Map<string, PodAssignment>();
    for (const pod of data?.pods ?? []) {
      map.set(pod.guid, pod);
    }
    return map;
  }, [data?.pods]);

  // !isLoading guard prevents "no pod" flicker on initial load
  const hasPodAssignment = !isLoading && (data?.pods?.length ?? 0) > 0;

  return {
    data,
    pods: data?.pods,
    podMap,
    hasPodAssignment,
    isLoading,
    isError,
  };
};
